import {Box, Chip, SvgIcon} from '@mui/material';
import * as React from 'react';
import * as _ from 'lodash';
import {useEffect, useState} from "react";
import moment from "moment/moment";
import {
  AgentByIdDocument,
  AgentByIdQuery, AgentByIdQueryVariables,
  darkenHexColor,
  labelByStatus,
  lightenHexColor,
  TagsDocument,
  TagsQuery
} from '..';
import { getUserName } from '@coverright/utils';
import { ApolloClient, useApolloClient } from '@apollo/client';
import { PeriodDateRangeValue } from '../../features';
import { DateRangeInput } from '@coverright/data-access/types/enrollment';

interface Props<T> {
  filter?: { [key: string]: any },
  onRemoveFilterClick?: (filterName: keyof T) => void,
  nameMapFunc?: (filterName: keyof T) => string,
}

export function FilterBadges<T>({filter, onRemoveFilterClick, nameMapFunc = n => n.toString()}: Props<T>) {
  const [badges, setBadges] = useState<React.ReactNode[]>([]);
  const apolloClient = useApolloClient();

  useEffect(() => {
    (async () => {
      const result = [];
      let i = 0;
      for (const filterKey in filter) {
        if (filter[filterKey] !== undefined && filter[filterKey] !== null && filter[filterKey] !== '') {
          if (Array.isArray(filter[filterKey]) && !filter[filterKey].length) {
            continue;
          }
          result.push(
            getFilter(
              i++,
              await mapFilterLabel(nameMapFunc(filterKey as any), filter[filterKey], apolloClient),
              onRemoveFilterClick ? () => onRemoveFilterClick(filterKey as keyof T) : undefined
            )
          )

        }
      }
      setBadges(result);
    })()
  }, [filter, apolloClient]);

  return <>
    {!!badges.length && <Box sx={{flexWrap: 'wrap', display: 'flex', gap: 1, pb: 1}}>
      {badges}
    </Box>}
  </>
}

const colors = ['#FF5733', '#33FF57', '#3357FF', '#F033FF', '#33FFF0', '#FF33F0', '#F0FF33']

const getFilter = (index: number, title: string, onDelete?: () => void) => (
  <Chip sx={{ backgroundColor: lightenHexColor(colors[index], 80), color: darkenHexColor(colors[index], 80), border: `1px solid ${darkenHexColor(colors[index], 20)}`}}
        key={title}
        label={title}
        onDelete={onDelete}
        deleteIcon={
          <SvgIcon sx={{width: 16, height: 16, fill: darkenHexColor(colors[index], 60)}}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="current" viewBox="0 0 256 256" className="MuiChip-deleteIcon MuiChip-deleteIconMedium MuiChip-deleteIconColorDefault MuiChip-deleteIconFilledColorDefault"><path d="M216,40H68.53a16.12,16.12,0,0,0-13.72,7.77L9.14,123.88a8,8,0,0,0,0,8.24l45.67,76.11h0A16.11,16.11,0,0,0,68.53,216H216a16,16,0,0,0,16-16V56A16,16,0,0,0,216,40ZM165.66,146.34a8,8,0,0,1-11.32,11.32L136,139.31l-18.35,18.35a8,8,0,0,1-11.31-11.32L124.69,128l-18.35-18.34a8,8,0,1,1,11.31-11.32L136,116.69l18.34-18.35a8,8,0,0,1,11.32,11.32L147.31,128Z"></path></svg>
          </SvgIcon>} />
)


const mapFilterLabel = async (name: string, value: any, apolloClient: ApolloClient<any>) => {

  const filterName = _.upperFirst(_.lowerCase(_.kebabCase(name)))
  switch (name) {
    case "tags": {
      const {data} = await apolloClient.query<TagsQuery>({ query: TagsDocument });
      return `${filterName}: ${value.map((id: string) => data?.tags.find(t => t.id === id)?.name).join(', ')}`;
    }
    case "status": return `${filterName}: ${labelByStatus(value)}`;
    case "Created":
    case "Next contact date":
    case "dueDateRange":
    case "birthDate": {
      return `${filterName}: ${dateRangeStr(value)}`;
    }
    case "agentId": {
      const {data} = await apolloClient.query<AgentByIdQuery, AgentByIdQueryVariables>({
        query: AgentByIdDocument,
        variables: {id: value}
      })
      return `Agent: ${data?.agentById ? getUserName(data.agentById) : 'unknown'}`
    }
    case "adviserEmail": return `Financial advisor: ${value}`;
    default: {
      if (typeof value === 'boolean') {
        return `${filterName}: ${value ? 'Yes' : 'No'}`;
      }
      return `${filterName}: ${value}`;
    }
  }
}

const dateRangeStr = (dateRange: DateRangeInput | PeriodDateRangeValue) => {
  const renderRange = (range: DateRangeInput) => {
    if (!range.from) {
      return 'Not selected';
    }
    return moment(range.from).format('L') + ' - ' + moment(range.to).format('L')
  }
  if (isDateRange(dateRange)) {
    return renderRange(dateRange);
  } else {
    if (dateRange.range) {
      return renderRange(dateRange.range);
    } else if (dateRange.period) {
      return `${dateRange.future ? 'Next' : 'Last'} ${dateRange.period} days`;
    }
  }
  console.warn('dateRangeStr', dateRange);
  return 'Unknown';
}

const isDateRange = (value: any): value is DateRangeInput => value.to || value.from
